import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import isEmpty from 'lodash/isEmpty';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 48;

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryFavouritesError: null,
  currentPageResultIds: [],
};

const resultIds = data => data.data.map(l => l.id);

export const queryFavouriteListingsPayloadCreater = (
  queryParams,
  { extra: sdk, dispatch, rejectWithValue, getState }
) => {
  const { currentUser } = getState().user;
  const { favourites } = currentUser?.attributes.profile.privateData || {};

  if (isEmpty(favourites)) {
    return {
      data: {
        data: [],
        included: [],
        meta: { totalItems: 0 },
      },
    };
  }

  const { perPage, include, ...rest } = queryParams;
  const params = { ids: favourites, include: ['author', ...include], ...rest, perPage };

  return sdk.listings
    .query(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      return response;
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const queryFavouriteListingsThunk = createAsyncThunk(
  'app/FavouriteListingsPage/queryFavouriteListings',
  queryFavouriteListingsPayloadCreater
);

const favouriteListingsSlice = createSlice({
  name: 'FavouriteListingsPage',
  initialState: initialState,
  reducer: {},
  extraReducers: builder => {
    builder
      .addCase(queryFavouriteListingsThunk.pending, (state, action) => {
        state.queryParams = action.meta.arg;
        state.queryInProgress = true;
        state.queryFavouritesError = null;
        state.currentPageResultIds = [];
      })
      .addCase(queryFavouriteListingsThunk.fulfilled, (state, action) => {
        state.currentPageResultIds = resultIds(action.payload.data);
        state.pagination = action.payload.data.meta;
        state.queryInProgress = false;
      })
      .addCase(queryFavouriteListingsThunk.rejected, (state, action) => {
        console.error(action.payload || action.error);
        state.queryInProgress = false;
        state.queryFavouritesError = action.payload;
      });
  },
});

export default favouriteListingsSlice.reducer;

export const loadData = (params, search, config) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return queryFavouriteListingsThunk({
    ...queryParams,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['images'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  });
};
